import { convertDate, displayDateFormat } from '@/constants/dates';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconAlertCircle,
  IconArrowDown,
  IconArrowRight,
  Text,
  useDisclosure,
} from '@cmpsr/components';
import ModalTestResults from '../../../modal-test-results/modal-test-results';
import { CompletedTestPlan } from '../../types';

export default function CompletedTest({
  updateAt,
  id,
  followUpNeeded,
  name,
  reducedRisk,
  rawAnswers,
}: CompletedTestPlan) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // HACK: a user may mark the item as completed without answering the survey.
  // This is a quick fix to hide the view results button when there are no answers.
  // The problem is made more complex because the testing plan "To Do" tab is driven by the
  // "user_test_suggestion" table, and the "Completed" tab is driven by the "user_screening_questionnaire_answers" table.
  const hasSurveyAnswers = Object.keys(rawAnswers).length > 0;

  return (
    <Flex
      data-testid={`completed-test-${id}`}
      as="article"
      alignItems="flex-start"
      gap={5}
      position="relative"
      maxW={{ base: '100%', lg: '780px' }}
      p={{ base: 5, lg: 6 }}
      pr="8 !important"
      border="1px solid"
      borderColor="ui-element-divider"
      borderRadius="radii-3"
      _focus={{ boxShadow: 'none' }}
      {...(hasSurveyAnswers && {
        _hover: { bg: 'background-container-hover' },
        _active: { bg: 'background-container-pressed' },
        cursor: 'pointer',
        onClick: onOpen,
      })}
    >
      {isOpen && <ModalTestResults isOpen={isOpen} onClose={onClose} idTestResults={id} />}
      {followUpNeeded ? (
        <Badge variant="solid" status="error" py={1} px={2} borderRadius="radii-2">
          <IconAlertCircle color="text-error" size="m" />
        </Badge>
      ) : !!reducedRisk && reducedRisk > 0 ? (
        <Box data-testid="reduced-risk-badge">
          <Badge
            status="success"
            color="text-success"
            py={1}
            px={2}
            borderRadius="radii-1"
            leadingIcon={<IconArrowDown size="xs" />}
          >
            <Text color="text-success" variant="text-body-floating-label-bold">
              {reducedRisk}
            </Text>
          </Badge>
        </Box>
      ) : null}
      <Box>
        <Text variant="text-body-meta-regular">{convertDate(updateAt, displayDateFormat)}</Text>
        <Box>
          <Text variant="text-body-regular" display="inline" mr={1}>
            You completed this {name}.
          </Text>
          <Button variant="link-primary" size="s" borderBottom="1px solid" borderRadius={0} onClick={onOpen}>
            {hasSurveyAnswers && (
              <Text variant="text-body-medium" lineHeight="1.1rem">
                View Results
              </Text>
            )}
          </Button>
        </Box>
        {followUpNeeded && (
          <Flex
            gap={{ base: 'spacer-2', lg: 'spacer-4' }}
            alignItems={{ base: 'flex-start', lg: 'center' }}
            flexDirection={{ base: 'column', lg: 'row' }}
            mt={2}
          >
            <Badge status="error" color="text-error" py={0.5} px={2} borderRadius="radii-1">
              Follow Up Needed
            </Badge>
          </Flex>
        )}
      </Box>
      {hasSurveyAnswers && (
        <IconArrowRight
          position="absolute"
          right="spacer-2"
          top="50%"
          transform="translateY(-50%)"
          color="text-primary"
        />
      )}
    </Flex>
  );
}
